package com.kinoshazam.screens.main.continueWatching

import androidx.compose.foundation.ExperimentalFoundationApi
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.combinedClickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyRow
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.ModalBottomSheet
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import com.kinoshazam.screens.main.medium.LeftAlignedCarouselRowV3
import com.kinoshazam.uiKit.components.carousel.CarouselNavButton
import com.kinoshazam.uiKit.components.image.ImageUiKitState
import com.kinoshazam.uiKit.components.image.UiKitImage
import com.kinoshazam.uiKit.extension.shimmer.shimmer
import com.kinoshazam.uiKit.theme.BoldMedium
import com.kinoshazam.uiKit.theme.Medium
import com.kinoshazam.uiKit.theme.UiKitTheme
import com.kinoshazam.view.OnBackPressed
import features.watch.model.WatchEntry
import kinoshazam.resources.generated.resources.Res
import kinoshazam.resources.generated.resources.continue_watching
import kinoshazam.resources.generated.resources.film_label
import kinoshazam.resources.generated.resources.left
import kinoshazam.resources.generated.resources.movie
import kinoshazam.resources.generated.resources.remaining_min
import kinoshazam.resources.generated.resources.remove_from_continue
import kinoshazam.resources.generated.resources.right
import kinoshazam.resources.generated.resources.season_episode
import kinoshazam.resources.generated.resources.watched_mark
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource
import kotlin.math.roundToInt

private val CompactCardWidth = 280.dp
private val CompactCardHeight = 104.dp
private val CardCorner = RoundedCornerShape(8.dp)
private const val WideBannerAspect = 300f / 170f
private const val WideSlots = 3.2f

enum class ContinueHideMode { Close, Sheet, Tv }

@OptIn(ExperimentalFoundationApi::class, ExperimentalMaterial3Api::class)
@Composable
fun ContinueWatchingSection(
    items: List<WatchEntry>,
    wide: Boolean,
    hideMode: ContinueHideMode,
    loading: Boolean,
    onOpen: (WatchEntry) -> Unit,
    onHide: (WatchEntry) -> Unit,
    onRequestHide: (WatchEntry) -> Unit = {},
    modifier: Modifier = Modifier,
) {
    if (!loading && items.isEmpty()) return
    var pendingHide by remember { mutableStateOf<WatchEntry?>(null) }
    OnBackPressed(enabled = pendingHide != null) { pendingHide = null }
    val sheetEntry = pendingHide
    if (hideMode == ContinueHideMode.Sheet && sheetEntry != null) {
        ModalBottomSheet(
            containerColor = UiKitTheme.colors.color_4,
            onDismissRequest = { pendingHide = null },
        ) {
            Text(
                text = stringResource(Res.string.remove_from_continue),
                color = UiKitTheme.colors.color_2,
                style = Medium,
                modifier = Modifier
                    .fillMaxWidth()
                    .clickable {
                        pendingHide = null
                        onHide(sheetEntry)
                    }
                    .padding(horizontal = 16.dp, vertical = 16.dp)
                    .navigationBarsPadding(),
            )
        }
    }
    Column(modifier = modifier.fillMaxWidth()) {
        Text(
            text = stringResource(Res.string.continue_watching),
            color = UiKitTheme.colors.color_2,
            style = BoldMedium,
            modifier = Modifier.padding(start = 16.dp, top = 20.dp, end = 16.dp, bottom = 8.dp),
        )
        if (wide) {
            WideContinueRow(
                items = carouselItems(items, loading),
                loading = loading,
                hideMode = hideMode,
                onOpen = onOpen,
                onHide = onHide,
                onRequestHide = onRequestHide,
                onPendingHide = { pendingHide = it },
            )
        } else {
            LazyRow(
                contentPadding = PaddingValues(horizontal = 16.dp),
                horizontalArrangement = Arrangement.spacedBy(8.dp),
            ) {
                if (loading) {
                    items(2) {
                        ContinueSkeleton(Modifier.size(CompactCardWidth, CompactCardHeight))
                    }
                } else {
                    items(
                        items = items,
                        key = { "${it.videoId}:${it.seasonNumber}:${it.episodeNumber}" },
                    ) { entry ->
                        Box(Modifier.size(CompactCardWidth, CompactCardHeight)) {
                            CompactContinueCard(
                                entry = entry,
                                onOpen = { onOpen(entry) },
                                onLongPress = { pendingHide = entry },
                            )
                        }
                    }
                }
            }
        }
    }
}

private fun carouselItems(items: List<WatchEntry>, loading: Boolean): List<WatchEntry?> {
    return if (loading) listOf(null) else items
}

@OptIn(ExperimentalFoundationApi::class)
@Composable
private fun WideContinueRow(
    items: List<WatchEntry?>,
    loading: Boolean,
    hideMode: ContinueHideMode,
    onOpen: (WatchEntry) -> Unit,
    onHide: (WatchEntry) -> Unit,
    onRequestHide: (WatchEntry) -> Unit,
    onPendingHide: (WatchEntry) -> Unit,
) {
    BoxWithConstraints(Modifier.fillMaxWidth()) {
        val navButton = 48.dp
        val sidePadding = 24.dp
        val spacing = 20.dp
        val center = (maxWidth - navButton * 2).coerceAtLeast(0.dp)
        val available = (center - sidePadding * 2).coerceAtLeast(0.dp)
        val cardWidth = ((available + spacing) / WideSlots - spacing).coerceAtLeast(180.dp)
        val cardHeight = cardWidth / WideBannerAspect
        LeftAlignedCarouselRowV3(
            items = items,
            itemWidth = cardWidth,
            itemHeight = cardHeight,
            leftContent = { enabled, onClick ->
                CarouselNavButton(
                    painter = painterResource(Res.drawable.left),
                    enabled = enabled && !loading,
                    contentDescription = "Prev",
                    onClick = onClick,
                )
            },
            rightContent = { enabled, onClick ->
                CarouselNavButton(
                    painter = painterResource(Res.drawable.right),
                    enabled = enabled && !loading,
                    contentDescription = "Next",
                    onClick = onClick,
                )
            },
        ) { _, entry, _ ->
            if (entry == null) {
                ContinueSkeleton(modifier = Modifier.fillMaxSize())
            } else {
                WideContinueCard(
                    entry = entry,
                    showClose = hideMode == ContinueHideMode.Close,
                    onOpen = { onOpen(entry) },
                    onHide = {
                        when (hideMode) {
                            ContinueHideMode.Sheet -> onPendingHide(entry)
                            ContinueHideMode.Tv -> onRequestHide(entry)
                            ContinueHideMode.Close -> onHide(entry)
                        }
                    },
                    onLongPress = when (hideMode) {
                        ContinueHideMode.Sheet -> {
                            { onPendingHide(entry) }
                        }
                        ContinueHideMode.Tv -> {
                            { onRequestHide(entry) }
                        }
                        ContinueHideMode.Close -> null
                    },
                )
            }
        }
    }
}

@Composable
private fun ContinueSkeleton(modifier: Modifier = Modifier) {
    Box(
        modifier
            .clip(CardCorner)
            .background(UiKitTheme.colors.color_4)
            .shimmer(),
    )
}

@OptIn(ExperimentalFoundationApi::class)
@Composable
private fun CompactContinueCard(
    entry: WatchEntry,
    onOpen: () -> Unit,
    onLongPress: () -> Unit,
) {
    Column(
        modifier = Modifier
            .fillMaxSize()
            .clip(CardCorner)
            .background(UiKitTheme.colors.color_4)
            .combinedClickable(onClick = onOpen, onLongClick = onLongPress),
    ) {
        Row(modifier = Modifier.weight(1f)) {
            Poster(
                url = entry.imageUrl,
                modifier = Modifier.width(72.dp).fillMaxHeight(),
            )
            Column(
                modifier = Modifier
                    .weight(1f)
                    .padding(horizontal = 12.dp, vertical = 10.dp),
                verticalArrangement = Arrangement.Center,
            ) {
                Text(
                    text = entry.title.orEmpty(),
                    color = UiKitTheme.colors.color_2,
                    style = BoldMedium,
                    maxLines = 2,
                    overflow = TextOverflow.Ellipsis,
                )
                Spacer(Modifier.height(4.dp))
                Text(
                    text = entry.subtitle(),
                    color = UiKitTheme.colors.color_1,
                    style = Medium,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                )
            }
        }
        ProgressTrack(entry.progress)
    }
}

@OptIn(ExperimentalFoundationApi::class)
@Composable
private fun WideContinueCard(
    entry: WatchEntry,
    showClose: Boolean,
    onOpen: () -> Unit,
    onHide: () -> Unit,
    onLongPress: (() -> Unit)?,
) {
    Box(
        modifier = Modifier
            .fillMaxSize()
            .clip(CardCorner)
            .combinedClickable(
                onClick = onOpen,
                onLongClick = onLongPress,
            ),
    ) {
        Poster(
            url = entry.backdropUrl ?: entry.imageUrl,
            modifier = Modifier.fillMaxSize(),
        )
        Box(
            modifier = Modifier
                .fillMaxSize()
                .background(
                    Brush.verticalGradient(
                        colors = listOf(Color.Transparent, Color.Black.copy(alpha = 0.82f)),
                    ),
                ),
        )
        Column(
            modifier = Modifier
                .align(Alignment.BottomStart)
                .padding(12.dp),
        ) {
            Text(
                text = entry.title.orEmpty(),
                color = Color.White,
                style = BoldMedium,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis,
            )
            Text(
                text = entry.subtitle(),
                color = UiKitTheme.colors.color_1,
                style = Medium,
                maxLines = 1,
            )
            Spacer(Modifier.height(8.dp))
            ProgressTrack(entry.progress)
        }
        if (showClose) {
            Text(
                text = "×",
                color = Color.White,
                style = BoldMedium,
                modifier = Modifier
                    .align(Alignment.TopEnd)
                    .clickable(onClick = onHide)
                    .padding(8.dp),
            )
        }
    }
}

@Composable
private fun Poster(url: String?, modifier: Modifier) {
    UiKitImage(
        state = ImageUiKitState(
            url = url,
            placeholder = painterResource(Res.drawable.movie),
            contentScale = ContentScale.Crop,
        ),
        modifier = modifier,
    )
}

@Composable
private fun ProgressTrack(progress: Double) {
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .height(3.dp)
            .background(Color.White.copy(alpha = 0.25f)),
    ) {
        Box(
            modifier = Modifier
                .fillMaxWidth(progress.toFloat().coerceIn(0f, 1f))
                .height(3.dp)
                .background(UiKitTheme.colors.color_1),
        )
    }
}

@Composable
private fun WatchEntry.subtitle(): String {
    if (isTvShow && (seasonNumber > 0 || episodeNumber > 0)) {
        return stringResource(Res.string.season_episode, seasonNumber, episodeNumber)
    }
    if (status == WatchEntry.STATUS_COMPLETED) return stringResource(Res.string.watched_mark)
    if (durationSec > 0 && progress > 0.0) {
        val left = ((1.0 - progress) * durationSec / 60.0).roundToInt().coerceAtLeast(1)
        return stringResource(Res.string.remaining_min, left)
    }
    return stringResource(Res.string.film_label)
}
